import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    EverbridgeLoadState,
    EverbridgeMapping,
    EverbridgeMappingDefault,
    EverbridgeMappingFormState,
    EverbridgeMappingID,
    EverbridgePassport,
    EverbridgePassportDefault,
    EverbridgePassportFormState,
    EverbridgePassportHeader,
    EverbridgePassportID,
} from "userful-chronos-app-common-js/dist/models/integration/everbridge";
import {
    requestAddPassport,
    requestListPassports,
    requestRemovePassport,
    requestUpdatePassport,
    requestAddMapping,
    requestListMappings,
    requestRemoveMapping,
    requestUpdateMapping,
} from "./msgs/everbridgeMsgSender";

const initialState: {
    passports: EverbridgePassport[];
    loadState: EverbridgeLoadState;
    passport: EverbridgePassport;
    copyPassport: EverbridgePassport;
    formType: EverbridgePassportFormState;
    openForm: boolean;
    mappings: EverbridgeMapping[];
    mappingLoadState: EverbridgeLoadState;
    mapping: EverbridgeMapping;
    copyMapping: EverbridgeMapping;
    mappingFormType: EverbridgeMappingFormState;
    mappingOpenForm: boolean;
    trigerCreateMapping: boolean;
} = {
    passports: [],
    loadState: "RENDER",
    passport: EverbridgePassportDefault(),
    copyPassport: EverbridgePassportDefault(),
    formType: "Create",
    openForm: false,
    mappings: [],
    mappingLoadState: "RENDER",
    mapping: EverbridgeMappingDefault(),
    copyMapping: EverbridgeMappingDefault(),
    mappingFormType: "Create",
    mappingOpenForm: false,
    trigerCreateMapping: false,
};

export const everbridgeSlice = createSlice({
    name: "everbridgeSlice",
    initialState,
    reducers: {
        setPassports: (state, action: PayloadAction<EverbridgePassport[]>) => {
            state.passports = action.payload;
        },
        addOrUpdatePassport: (state, action: PayloadAction<EverbridgePassport>) => {
            const index = state.passports.findIndex((item) => item.id.value === action.payload.id.value);
            if (index === -1) {
                state.passports = [...state.passports, { ...action.payload }];
            } else {
                state.passports = [
                    ...state.passports.slice(0, index),
                    { ...action.payload },
                    ...state.passports.slice(index + 1),
                ];
            }
        },
        removePassport: (state, action: PayloadAction<EverbridgePassportID>) => {
            state.passports = state.passports.filter((item) => item.id.value !== action.payload.value);
        },
        getPassportsFromServer: () => {
            requestListPassports();
        },
        addPassportToServer: (_state, action: PayloadAction<EverbridgePassportHeader>) => {
            requestAddPassport(action.payload);
        },
        updatePassportToServer: (_state, action: PayloadAction<EverbridgePassportHeader>) => {
            requestUpdatePassport(action.payload);
        },
        removePassportToServer: (_state, action: PayloadAction<EverbridgePassportID>) => {
            requestRemovePassport(action.payload);
        },
        changeLoadState: (state, action: PayloadAction<EverbridgeLoadState>) => {
            state.loadState = action.payload;
        },
        setPassport: (state, action: PayloadAction<EverbridgePassport>) => {
            state.passport = action.payload;
        },
        setCopyPassport: (state, action: PayloadAction<EverbridgePassport>) => {
            state.copyPassport = action.payload;
        },
        setFormType: (state, action: PayloadAction<EverbridgePassportFormState>) => {
            state.formType = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        setMappings: (state, action: PayloadAction<EverbridgeMapping[]>) => {
            state.mappings = action.payload;
        },
        addOrUpdateMapping: (state, action: PayloadAction<EverbridgeMapping>) => {
            const index = state.mappings.findIndex((item) => item.id.value === action.payload.id.value);
            if (index === -1) {
                state.mappings = [...state.mappings, { ...action.payload }];
            } else {
                state.mappings = [
                    ...state.mappings.slice(0, index),
                    { ...action.payload },
                    ...state.mappings.slice(index + 1),
                ];
            }
        },
        removeMapping: (state, action: PayloadAction<EverbridgeMappingID>) => {
            state.mappings = state.mappings.filter((item) => item.id.value !== action.payload.value);
        },
        getMappingsFromServer: (_state, action: PayloadAction<EverbridgePassportID | undefined>) => {
            requestListMappings(action.payload);
        },
        addMappingToServer: (_state, action: PayloadAction<EverbridgeMapping>) => {
            requestAddMapping(action.payload);
        },
        updateMappingToServer: (_state, action: PayloadAction<EverbridgeMapping>) => {
            requestUpdateMapping(action.payload);
        },
        removeMappingToServer: (_state, action: PayloadAction<EverbridgeMappingID>) => {
            requestRemoveMapping(action.payload);
        },
        changeMappingLoadState: (state, action: PayloadAction<EverbridgeLoadState>) => {
            state.mappingLoadState = action.payload;
        },
        setMapping: (state, action: PayloadAction<EverbridgeMapping>) => {
            state.mapping = action.payload;
        },
        setCopyMapping: (state, action: PayloadAction<EverbridgeMapping>) => {
            state.copyMapping = action.payload;
        },
        setMappingFormType: (state, action: PayloadAction<EverbridgeMappingFormState>) => {
            state.mappingFormType = action.payload;
        },
        setMappingOpenForm: (state, action: PayloadAction<boolean>) => {
            state.mappingOpenForm = action.payload;
        },
        setTriggerCreateMapping: (state) => {
            state.trigerCreateMapping = !state.trigerCreateMapping;
        },
    },
});

export const everbridgeActions = everbridgeSlice.actions;

export default everbridgeSlice.reducer;
